'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import {
    Box,
    Flex,
    Text,
    Heading,
    VStack,
    HStack,
    Spinner,
    Button,
    Icon,
    useToast,
} from '@chakra-ui/react';
import { BsChatSquareText, BsPlusCircle } from 'react-icons/bs';
import { AnimatePresence } from 'framer-motion';
import { motion } from 'framer-motion';

// Import custom components
import { ConversationsList } from './components/ConversationsList';
import {  } from './components/ChatHeader';
import { ChatMessage } from './components/ChatMessage';
import { ChatInput } from './components/ChatInput';
import BoxWrapper from '../../components/BoxWrapper/BoxWrapper';
import Header from '../../components/Header';
import { fetchWrapper } from '../../helpers/fetch-wrapper';

const MotionFlex = motion(Flex);

const BASE_URL = process.env.REACT_APP_API_URL;

function formatTimestamp(isoString) {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatTime(isoString) {
    if (!isoString) return new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    return new Date(isoString).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

function normalizeConversation(conv) {
    return {
        id: conv._id || conv.id,
        title: conv.title || 'New Conversation',
        timestamp: formatTimestamp(conv.updatedAt || conv.createdAt),
    };
}

function normalizeMessage(turn) {
    const isAssistant = turn.role === 'assistant';
    const resp = turn.response ?? {};
    return {
        id: turn._id || turn.id || String(Date.now() + Math.random()),
        type: isAssistant ? 'ai' : 'user',
        content: turn.content || '',
        timestamp: formatTime(turn.createdAt),
        renderSpec: resp.renderSpec ?? null,
        warnings: resp.warnings ?? [],
        followups: resp.followups ?? [],
    };
}

export function Asklytics() {
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [isTyping, setIsTyping] = useState(false);
    const [conversationsLoading, setConversationsLoading] = useState(false);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [limitReached, setLimitReached] = useState(false);
    const messagesEndRef = useRef(null);
    const toast = useToast();

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const loadConversations = useCallback(async (autoSelectFirst = false) => {
        setConversationsLoading(true);
        try {
            const response = await fetchWrapper.get(
                `${BASE_URL}/asklytics/conversations?limit=30&skip=0`
            );
            const list = Array.isArray(response)
                ? response
                : (response.data || response.conversations || []);
            const normalized = list.map(normalizeConversation);
            setConversations(normalized);
            if (autoSelectFirst && normalized.length > 0) {
                setSelectedConversation(normalized[0].id);
            }
        } catch {
            // keep empty list on error
        } finally {
            setConversationsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadConversations(true);
    }, [loadConversations]);

    const loadConversationMessages = useCallback(async (conversationId) => {
        setMessagesLoading(true);
        setMessages([]);
        try {
            const response = await fetchWrapper.get(
                `${BASE_URL}/asklytics/conversations/${conversationId}`
            );
            const turns = response?.conversation?.turns ?? [];
            setMessages(turns.map(normalizeMessage));
        } catch {
            setMessages([]);
        } finally {
            setMessagesLoading(false);
        }
    }, []);

    useEffect(() => {
        if (selectedConversation) {
            setLimitReached(false);
            loadConversationMessages(selectedConversation);
        }
    }, [selectedConversation, loadConversationMessages]);

    const createConversation = async (title = '') => {
        const response = await fetchWrapper.post(
            `${BASE_URL}/asklytics/conversations`,
            { title }
        );
        // Handle: { data: { _id, title } } or { _id, title } or { conversation: { _id, title } }
        const conv = response?.data ?? response?.conversation ?? response;
        const normalized = normalizeConversation(conv);
        setConversations((prev) => [normalized, ...prev]);
        return normalized.id;
    };

    const handleSendMessage = async (question) => {
        const userMessage = {
            id: Date.now().toString(),
            type: 'user',
            content: question,
            timestamp: new Date().toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
            }),
        };

        setMessages((prev) => [...prev, userMessage]);
        setIsTyping(true);

        try {
            let conversationId = selectedConversation;
            if (!conversationId) {
                conversationId = await createConversation(question);
                setSelectedConversation(conversationId);
            }

            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const response = await fetchWrapper.post(`${BASE_URL}/asklytics/ask`, {
                question,
                conversationId,
                timeRange: { type: 'relative', value: '7d' },
                timezone,
            });

            // Check conversation limit before rendering the AI reply
            if (response.conversationSave?.error === 'conversation_limit_reached') {
                setLimitReached(true);
                toast({
                    title: 'Conversation limit reached',
                    description: 'Start a new conversation to continue asking questions.',
                    status: 'warning',
                    duration: 5000,
                    isClosable: true,
                    position: 'top-right',
                });
            }

            const aiMessage = {
                id: (Date.now() + 1).toString(),
                type: 'ai',
                content: '',
                timestamp: new Date().toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                }),
                renderSpec: response.renderSpec,
                warnings: response.warnings ?? [],
                followups: response.followups ?? [],
            };
            setMessages((prev) => [...prev, aiMessage]);

            // Refresh conversations list to pick up updated titles/previews
            loadConversations();
        } catch {
            const errorMessage = {
                id: (Date.now() + 1).toString(),
                type: 'ai',
                content: 'Something went wrong while analyzing your data. Please try again.',
                timestamp: new Date().toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                }),
                warnings: [],
                followups: [],
            };
            setMessages((prev) => [...prev, errorMessage]);
        } finally {
            setIsTyping(false);
        }
    };

    const handleNewConversation = async () => {
        setMessages([]);
        setSelectedConversation(null);
        setLimitReached(false);
        try {
            const id = await createConversation('');
            setSelectedConversation(id);
        } catch {
            // fall through — conversation will be created on first message
        }
    };

    const handleTitleUpdate = (newTitle) => {
        setConversations((prev) =>
            prev.map((conv) =>
                conv.id === selectedConversation ? { ...conv, title: newTitle } : conv
            )
        );
    };

    const selectedTitle =
        conversations.find((c) => c.id === selectedConversation)?.title || 'New Conversation';

    return (
        <>
            <Header />
            <BoxWrapper>
                <Box>
                    {/* Header */}
                    <Flex py={4} borderBottomWidth="1px" bg="white" alignItems="center" gap={3}>
                        <Flex
                            w={10}
                            h={10}
                            borderRadius="lg"
                            bg="var(--brand-primary)"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <Box as={BsChatSquareText} w={5} h={5} color="white" />
                        </Flex>
                        <Box>
                            <HStack gap={2} alignItems="center">
                                <Heading as="h1" size="sm" color="gray.900">
                                    Asklytics
                                </Heading>
                                <Text fontSize="2xs" fontWeight="semibold" color="var(--brand-primary)" bg="blue.50" px={1.5} py={0.5} borderRadius="full" borderWidth="1px" borderColor="blue.200">
                                    Beta
                                </Text>
                            </HStack>
                            <Text fontSize="xs" color="gray.500">
                                Talk to Your Data
                            </Text>
                        </Box>
                    </Flex>

                    {/* Main Content */}
                    <Flex gap={2} py={4}>
                        {/* Left Sidebar - Conversations List */}
                        <ConversationsList
                            conversations={conversations}
                            selectedId={selectedConversation}
                            onSelect={setSelectedConversation}
                            onNewConversation={handleNewConversation}
                            isLoading={conversationsLoading}
                        />

                        {/* Main Chat Area */}
                        <Flex flex={1} h="calc(100vh - 160px)" direction="column" pl={2}>
                            {!selectedConversation ? (
                                /* Empty state — no conversation selected */
                                <Flex
                                    flex={1}
                                    direction="column"
                                    alignItems="center"
                                    justifyContent="center"
                                    gap={4}
                                >
                                    <Flex
                                        w={16}
                                        h={16}
                                        borderRadius="2xl"
                                        bg="blue.50"
                                        alignItems="center"
                                        justifyContent="center"
                                    >
                                        <Icon as={BsChatSquareText} boxSize={7} color="var(--brand-primary)" />
                                    </Flex>
                                    <Box textAlign="center">
                                        <Text fontWeight="bold" color="gray.800" fontSize="md">
                                            No conversation selected
                                        </Text>
                                        <Text fontSize="sm" color="gray.500" mt={1}>
                                            Start a new conversation to ask questions about your data
                                        </Text>
                                    </Box>
                                    <Button
                                        size="sm"
                                        bg="var(--brand-primary)"
                                        color="white"
                                        _hover={{ bg: 'var(--brand-primary-hover)' }}
                                        leftIcon={<BsPlusCircle />}
                                        onClick={handleNewConversation}
                                    >
                                        New Conversation
                                    </Button>
                                </Flex>
                            ) : (
                                <>
                                    {/* Messages Area */}
                                    <Box flex={1} overflowY="auto" pt={3}>
                                        {messagesLoading ? (
                                            <Flex justifyContent="center" pt={10}>
                                                <Spinner size="md" color="var(--brand-primary)" />
                                            </Flex>
                                        ) : (
                                            <VStack gap={3} align="stretch">
                                                <AnimatePresence>
                                                    {messages.map((message) => (
                                                        <ChatMessage key={message.id} message={message} />
                                                    ))}
                                                </AnimatePresence>

                                                {/* Typing Indicator */}
                                                {isTyping && (
                                                    <MotionFlex
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        justifyContent="flex-start"
                                                    >
                                                        <Box
                                                            bg="white"
                                                            borderWidth="1px"
                                                            borderColor="blue.100"
                                                            p={3}
                                                            borderRadius="lg"
                                                        >
                                                            <HStack gap={2}>
                                                                <Spinner size="sm" color="var(--brand-primary)" />
                                                                <Text fontSize="sm" color="gray.600" fontWeight="medium">
                                                                    Analyzing your data...
                                                                </Text>
                                                            </HStack>
                                                        </Box>
                                                    </MotionFlex>
                                                )}

                                                <Box ref={messagesEndRef} />
                                            </VStack>
                                        )}
                                    </Box>

                                    {/* Limit reached banner */}
                                    {limitReached && (
                                        <Flex
                                            align="center"
                                            justify="space-between"
                                            bg="orange.50"
                                            borderWidth="1px"
                                            borderColor="orange.200"
                                            borderRadius="md"
                                            px={4}
                                            py={2}
                                            mb={2}
                                        >
                                            <Text fontSize="sm" color="orange.700">
                                                Conversation limit reached. Start a new conversation to continue.
                                            </Text>
                                            <Button
                                                size="xs"
                                                bg="var(--brand-primary)"
                                                color="white"
                                                _hover={{ bg: 'var(--brand-primary-hover)' }}
                                                leftIcon={<BsPlusCircle />}
                                                onClick={handleNewConversation}
                                                ml={3}
                                                flexShrink={0}
                                            >
                                                New
                                            </Button>
                                        </Flex>
                                    )}

                                    {/* Input Bar */}
                                    <ChatInput onSend={handleSendMessage} disabled={isTyping || limitReached} />
                                </>
                            )}
                        </Flex>
                    </Flex>
                </Box>
            </BoxWrapper>
        </>
    );
}
